import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  alpha,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControlLabel,
  Grid,
  MenuItem,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  CheckCircle,
  Email as EmailIcon,
  HistoryOutlined,
  PictureAsPdf as PictureAsPdfIcon,
  ReceiptLongOutlined,
  BlockOutlined,
  RestartAltOutlined,
  LockOutlined,
  RadioButtonUnchecked,
  VisibilityOutlined,
  GroupsOutlined,
  ImageOutlined,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getInvoiceGroupById } from 'src/shared/services/invoiceGroupService';
import { createInvoiceTemplatePreviewDraft, sendInvoiceTemplateTestEmail } from 'src/shared/services/invoiceService';
import { getSelectionAssociations } from 'src/shared/services/memberService';
import { getAssociationLogoBlob } from 'src/shared/services/associationService';
import { MemberSearchFilter } from 'src/modules/member/models/memberSearchFilter';
import { MemberStatusEnum } from 'src/shared/models/enums/memberStatusEnum';
import { SelectionAssociation } from 'src/modules/sending/models/selectionAssociation';
import { AssociationAccountTypeEnum } from 'src/shared/models/enums/associationAccountType.enum';
import { isForbundCustomer } from 'src/modules/sending/functions/isForbundCustomer';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { cardStyle } from 'src/modules/sending/components/sendingWizardCardStyles';
import { SendingCopyTextsFromMissionModal } from 'src/modules/sending/components/sendingCopyTextsFromMissionModal';
import { SendingInvoiceMock } from 'src/modules/sending/components/sendingInvoiceMock';
import { useSendingWizard } from 'src/modules/sending/contexts/sendingWizardContext';
import { toCustomerSendDate } from 'src/modules/sending/functions/customerSendDate';

// Fakturablankett-val (inv_template.formTemplate). Vilka val som är tillåtna styrs av faktureringsgruppens kontotyp:
// Bankgiro-grupp får bara Bankgiro OCR, Plusgiro-grupp bara Plusgiro OCR. "Inget inbetalningskort" (blank) är alltid tillåtet.
interface BlankettOption {
  code: string;
  label: string;
  description: string;
}
const BLANK_OPTION: BlankettOption = {
  code: 'blank',
  label: 'Inget inbetalningskort',
  description: 'Fakturan skrivs ut utan avi – betalning sker via gruppens aktiva betalsätt.',
};
const accountTypeToBlankettOptions = (accountType: number | null): BlankettOption[] => {
  const opts: BlankettOption[] = [];
  // Bankgiro använder den nya blanketten "Bankgiro OCR 2024" (bgocr_2024), inte den gamla bgocr.
  if (accountType === AssociationAccountTypeEnum.Bankgiro)
    opts.push({ code: 'bgocr_2024', label: 'Bankgiro OCR', description: 'Inbetalningskort med OCR-referens, kopplat till Bankgiro.' });
  else if (accountType === AssociationAccountTypeEnum.Plusgiro)
    opts.push({ code: 'pgocr', label: 'Plusgiro OCR', description: 'Inbetalningskort med OCR-referens, kopplat till Plusgiro.' });
  opts.push(BLANK_OPTION);
  return opts;
};

// Steg 4: texterna för utskicket. En rad per sending – formulärkort till vänster (klar-markeras och blir grönt),
// förhandsvisning till höger i en blå box (lika hög som kortet, med scroll vid behov). E-post/Kivra-sendingen visas
// bara om den leveransmetoden är vald; fakturans innehåll alltid. "Jag kompletterar senare" → Komplettering behövs.
export const SendingWizardStep6Texts = () => {
  const { watch, setValue, getValues } = useFormContext();
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const {
    emailTextConfirmed,
    setEmailTextConfirmed,
    invoiceTextConfirmed,
    setInvoiceTextConfirmed,
    blankettConfirmed,
    setBlankettConfirmed,
    sendDate,
    setTextStepNav,
    dueDateFrom,
    dueDateTom,
    profileFilters,
    excludeProfileFilters,
    led,
  } = useSendingWizard();
  const [copyOpen, setCopyOpen] = useState(false);

  // Faktureringsgruppens kontotyp (Bankgiro/Plusgiro/IBAN) avgör vilka fakturablanketter som får väljas; kontonr visas i previewen.
  const ownerId = watch('owner_id') as number | undefined;
  const [accountType, setAccountType] = useState<number | null>(null);
  const [accountNr, setAccountNr] = useState<string>('');
  useEffect(() => {
    if (!ownerId || ownerId <= 0) {
      setAccountType(null);
      return;
    }
    (async () => {
      try {
        const res = await getInvoiceGroupById(ownerId);
        if (isMounted()) {
          setAccountType(res.data?.accountType ?? AssociationAccountTypeEnum.Unknown);
          setAccountNr(res.data?.accountNr ?? '');
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
          setAccountType(AssociationAccountTypeEnum.Unknown);
        }
      }
    })();
  }, [ownerId, isMounted]);

  const blankettOptions = accountTypeToBlankettOptions(accountType);
  const formTemplate = (watch('formTemplate') as string) || '';
  // När kontotypen laddats: förvälj OCR-blanketten för kontotypen om inget giltigt val finns (annars "Inget inbetalningskort").
  useEffect(() => {
    if (accountType == null) return;
    const opts = accountTypeToBlankettOptions(accountType);
    const current = (getValues('formTemplate') as string) || '';
    if (!opts.some((o) => o.code === current)) {
      setValue('formTemplate', opts.find((o) => o.code !== BLANK_OPTION.code)?.code ?? BLANK_OPTION.code);
    }
  }, [accountType, getValues, setValue]);
  // Sekventiell fliklista i Texter-steget – man klarmarkerar en flik i taget och hoppar vidare till nästa (samma
  // logik som val i tidigare steg). Sista fliken "preview" (Förhandsgranska) har inga fält att klarmarkera – där
  // ligger bara åtgärderna testutskick + förhandsgranska fakturan.
  type TextTabKey = 'email' | 'invoice' | 'blankett' | 'preview';
  const [textTab, setTextTab] = useState<TextTabKey>('email');
  const completeLater = !!watch('completeLater');
  const textsFromPrevious = !!watch('textsFromPrevious');
  const writeChosen = !!watch('textsWriteChosen');
  // Inget läge valt ännu → ingen lägesknapp förvald (kunden måste börja med att välja hur texterna ska läggas in).
  const noneChosen = !completeLater && !textsFromPrevious && !writeChosen;
  const deliveryOrder = ((watch('deliveryMethodOrder') as string) ?? '').split(',');
  // E-post/Kivra-texten behövs bara om något faktiskt skickas via e-post eller Kivra.
  const hasMail = deliveryOrder.includes('mail');
  const hasKivra = deliveryOrder.includes('kivra');
  const showEmail = hasMail || hasKivra;
  // Rubriken/etiketten anpassas efter valda kanaler: bara e-post, bara Kivra, eller båda.
  let emailTabLabel = 'E-post';
  let emailChannelTitle = 'e-postutskicket';
  let emailChannelMsg = 'e-postmeddelandet';
  if (hasMail && hasKivra) {
    emailTabLabel = 'E-post / Kivra';
    emailChannelTitle = 'e-post / Kivra-utskicket';
    emailChannelMsg = 'e-post- och Kivra-meddelandet';
  } else if (hasKivra && !hasMail) {
    emailTabLabel = 'Kivra';
    emailChannelTitle = 'Kivra-utskicket';
    emailChannelMsg = 'Kivra-meddelandet';
  }

  // E-post/Kivra-fliken finns bara vid e-post/Kivra-utskick.
  const tabOrder = [showEmail ? 'email' : null, 'invoice', led === 0 ? 'blankett' : null, 'preview'].filter(Boolean) as TextTabKey[];
  const confirmedOf: Record<string, boolean> = { email: emailTextConfirmed, invoice: invoiceTextConfirmed, blankett: blankettConfirmed };
  const isContentTab = (k: string) => k === 'email' || k === 'invoice' || k === 'blankett';
  // En flik är låst tills alla föregående innehållsflikar är klarmarkerade (tvingar ordningen).
  const isTabLocked = (key: string) => tabOrder.slice(0, tabOrder.indexOf(key as TextTabKey)).some((k) => isContentTab(k) && !confirmedOf[k]);
  // "Aktuell" flik = första ej klarmarkerade innehållsfliken; är allt klart landar man på Förhandsgranska.
  const firstIncompleteTab = tabOrder.find((k) => isContentTab(k) && !confirmedOf[k]) ?? 'preview';
  // Klampa användarens val: en borttagen eller ännu låst flik faller tillbaka på aktuell flik.
  let effectiveTextTab = textTab;
  if (!tabOrder.includes(effectiveTextTab) || isTabLocked(effectiveTextTab)) effectiveTextTab = firstIncompleteTab;
  // Marker högst upp i steget – används för att scrolla modalens innehåll upp när man går vidare till nästa flik.
  const topRef = useRef<HTMLDivElement | null>(null);
  // Scrolla närmaste scrollbara förälder (modalens body) till toppen så man ser att nästa flik aktiveras.
  const scrollStepToTop = useCallback(() => {
    let p: HTMLElement | null = topRef.current?.parentElement ?? null;
    while (p) {
      const oy = window.getComputedStyle(p).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) {
        p.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      p = p.parentElement;
    }
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  // Klarmarkera en flik → låt användaren se den gröna bocken en stund, byt sedan flik lugnt och scrolla upp.
  const goToNextTab = (key: TextTabKey) => {
    const next = tabOrder[tabOrder.indexOf(key) + 1];
    if (!next) return;
    window.setTimeout(() => {
      setTextTab(next);
      scrollStepToTop();
    }, 350);
  };
  const confirmEmail = (v: boolean) => {
    setEmailTextConfirmed(v);
    if (v) goToNextTab('email');
  };
  const confirmInvoice = (v: boolean) => {
    setInvoiceTextConfirmed(v);
    if (v) goToNextTab('invoice');
  };

  // Koppla flik-navigeringen till wizard-footerns Tillbaka/Fortsätt: när flikarna visas bläddrar footern mellan
  // flikarna (E-post → … → Förhandsgranska) och går först därefter till föregående/nästa steg. Fortsätt får bara
  // aktiveras när aktuell flik är klarmarkerad (eller man står på Förhandsgranska). navStateRef håller den färska
  // fliklistan så registrerade goNext/goPrev alltid utgår från aktuell effektiv flik.
  const navStateRef = useRef({ tabOrder, effectiveTextTab });
  navStateRef.current = { tabOrder, effectiveTextTab };
  const tabsShown = !completeLater && !noneChosen;
  const activeIdx = tabOrder.indexOf(effectiveTextTab);
  const atFirstTab = activeIdx <= 0;
  const atLastTab = activeIdx === tabOrder.length - 1;
  const activeTabConfirmed = isContentTab(effectiveTextTab) ? !!confirmedOf[effectiveTextTab] : true;
  const canForwardTab = atLastTab || activeTabConfirmed;
  const goPrevTab = useCallback(() => {
    const { tabOrder: to, effectiveTextTab: cur } = navStateRef.current;
    const prev = to[to.indexOf(cur) - 1];
    if (prev) {
      setTextTab(prev);
      scrollStepToTop();
    }
  }, [scrollStepToTop]);
  const goNextTab = useCallback(() => {
    const { tabOrder: to, effectiveTextTab: cur } = navStateRef.current;
    const nxt = to[to.indexOf(cur) + 1];
    if (nxt) {
      setTextTab(nxt);
      scrollStepToTop();
    }
  }, [scrollStepToTop]);
  useEffect(() => {
    setTextStepNav({ hasTabs: tabsShown, atFirstTab, atLastTab, canForward: canForwardTab, goNextTab, goPrevTab });
  }, [tabsShown, atFirstTab, atLastTab, canForwardTab, goNextTab, goPrevTab, setTextStepNav]);
  // Nollställ registreringen när steget lämnas så footern går tillbaka till vanlig steg-navigering.
  useEffect(() => () => setTextStepNav(null), [setTextStepNav]);

  // Kontotyp-etikett för blankett-fliken.
  let accountTypeLabel = 'okänd kontotyp';
  if (accountType === AssociationAccountTypeEnum.Bankgiro) accountTypeLabel = 'Bankgiro';
  else if (accountType === AssociationAccountTypeEnum.Plusgiro) accountTypeLabel = 'Plusgiro';
  else if (accountType === AssociationAccountTypeEnum.IBAN) accountTypeLabel = 'IBAN';

  const emailHeading = (watch('emailHeading') as string) ?? '';
  const emailText = (watch('emailText') as string) ?? '';
  const invoiceHeading = (watch('invoiceHeading') as string) ?? '';
  const invoiceText = (watch('invoiceText') as string) ?? '';

  // Logotyp på fakturan. Valet (förbundets centrala logga vs föreningarnas/sin egen logga) visas BARA för ett förbund
  // (flera föreningar – Organisation-modulen finns). Där kan alla användartyper välja. En enskild förening har bara en
  // logga → inget val visas och förbundets (centrala) logga används.
  // Bara en FÖRBUNDSanvändare (MEMBER_READ_ALL) representerar förbundet och kan välja förbundets centrala logga. En
  // förenings-/distriktsanvändare (READ_ASSOCIATION/READ_DISTRICT) använder alltid föreningens egna logga per mottagare –
  // även om hen har tillgång till FLERA föreningar (och därmed Organisation-modulen). Därför räcker inte
  // Organisation-modulen som signal; det är behörigheten som avgör.
  const isForbundsAnvandare = getPermission(currentUser, PermissionEnum.MemberReadAll);
  // Logotyp-valet visas bara för en förbundsanvändare på ett riktigt förbund (flera föreningar). Alla andra får inget val.
  const showLogoChoice = isForbundsAnvandare && isForbundCustomer(currentUser);
  // Default = förbundets logga (false); en odefinierad flagga tolkas som förbundets logga.
  const useLocalAssLogo = watch('useLocalAssLogo') === true;
  // Visas inget val: förbundsanvändare (enskild förening) → förbundets centrala logga (false); förenings-/distrikts-
  // användare → föreningens egna logga per mottagare (true).
  useEffect(() => {
    if (!showLogoChoice) {
      const forced = !isForbundsAnvandare;
      if (useLocalAssLogo !== forced) setValue('useLocalAssLogo', forced);
    }
  }, [showLogoChoice, isForbundsAnvandare, useLocalAssLogo, setValue]);
  // Förbundets (centrala) logga som data-URI (samma som headern visar) – visas i logotyp-valet.
  const forbundLogoSrc = currentUser?.logoBase64 ? `data:${currentUser?.logoMediaType || 'image/png'};base64,${currentUser.logoBase64}` : '';

  // Förhandsgranskning per förening: när föreningens egna logga är vald (useLocalAssLogo) kan användaren välja en av de
  // föreningar som urvalet omfattar och se både e-postutskicket och PDF:en med just den föreningens logga. Listan hämtas
  // via samma urvalsfilter som antal-frågan i Medlemmar-steget, så den matchar mottagarna exakt.
  const [foreningList, setForeningList] = useState<SelectionAssociation[]>([]);
  const [selectedLocalAssId, setSelectedLocalAssId] = useState<number | null>(null);
  const [foreningLogoSrc, setForeningLogoSrc] = useState('');
  // Nyckel som identifierar urvalet – hämtar bara om föreningslistan om urvalet ändrats.
  const selectionKey = `${dueDateFrom ? dueDateFrom.format('YYYYMMDD') : ''}|${dueDateTom ? dueDateTom.format('YYYYMMDD') : ''}|${profileFilters
    .map((p) => p.itemId)
    .sort((a, b) => a - b)
    .join(',')}|${excludeProfileFilters
    .map((p) => p.itemId)
    .sort((a, b) => a - b)
    .join(',')}`;
  const foreningKeyRef = useRef<string>('');
  useEffect(() => {
    // Hämta först när föreningsloggan är vald och man står på Förhandsgranska-fliken (undvik onödiga anrop).
    if (!useLocalAssLogo || effectiveTextTab !== 'preview') return;
    if (foreningKeyRef.current === selectionKey) return;
    foreningKeyRef.current = selectionKey;
    const filter: MemberSearchFilter = { status: [MemberStatusEnum.Paid, MemberStatusEnum.NotPaid] };
    if (dueDateFrom) filter.dueDateStart = dueDateFrom.format('YYYYMMDD');
    if (dueDateTom) filter.dueDateEnd = dueDateTom.format('YYYYMMDD');
    if (profileFilters.length) filter.profileItemIds = profileFilters.map((p) => p.itemId);
    if (excludeProfileFilters.length) filter.excludedProfileItemIds = excludeProfileFilters.map((p) => p.itemId);
    getSelectionAssociations(filter)
      .then((res) => {
        if (!isMounted()) return;
        const list = res.data ?? [];
        setForeningList(list);
        // Förvälj första föreningen med logga (annars första i listan) om inget giltigt val redan finns.
        setSelectedLocalAssId((prev) => {
          if (prev && list.some((a) => a.localAssId === prev)) return prev;
          const def = list.find((a) => a.hasLogo) ?? list[0];
          return def ? def.localAssId : null;
        });
      })
      .catch((e) => {
        if (isMounted()) ConsoleHelper.log(e);
      });
  }, [useLocalAssLogo, effectiveTextTab, selectionKey, dueDateFrom, dueDateTom, profileFilters, excludeProfileFilters, isMounted]);
  // Hämta den valda föreningens logga som object-URL (och städa upp föregående). Bara i enförenings-fallet – vid flera
  // föreningar visar skärm-previewen en generisk platshållare (loggan byts per medlem), så ingen logga behöver hämtas.
  useEffect(() => {
    if (!useLocalAssLogo || !selectedLocalAssId || foreningList.length > 1) {
      setForeningLogoSrc('');
      return undefined;
    }
    let url = '';
    getAssociationLogoBlob(selectedLocalAssId)
      .then((res) => {
        if (!isMounted()) return;
        url = window.URL.createObjectURL(res.data);
        setForeningLogoSrc(url);
      })
      .catch((e) => {
        if (isMounted()) {
          ConsoleHelper.log(e);
          setForeningLogoSrc('');
        }
      });
    return () => {
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [useLocalAssLogo, selectedLocalAssId, foreningList, isMounted]);
  // Omfattar urvalet flera föreningar? Då byts loggan per medlem och skärm-previewen visar en generisk platshållare –
  // man väljer förening först vid klick på testutskick/PDF. Vid exakt en förening förhandsgranskas den föreningens logga.
  const multipleForeningar = foreningList.length > 1;
  // Loggan som förhandsvisningarna (e-postkort + mock-PDF) ska visa: förbundets logga i förbundsläge; den enda
  // föreningens logga när urvalet bara har en förening; annars tom (generisk platshållare, eftersom loggan byts per medlem).
  let previewLogoSrc = forbundLogoSrc;
  if (useLocalAssLogo) previewLogoSrc = multipleForeningar ? '' : foreningLogoSrc;
  const selectedForening = foreningList.find((a) => a.localAssId === selectedLocalAssId) ?? null;
  // localAssId att skicka till API:et för riktig PDF/testutskick – bara i föreningsläge med en vald förening.
  const previewLocalAssId = useLocalAssLogo && selectedLocalAssId ? selectedLocalAssId : undefined;
  // Info-banner överst i Förhandsgranska-fliken – visas bara när urvalet omfattar FLERA föreningar, för att förklara att
  // man väljer förening i samband med testutskick/PDF (skärm-previewen visar en generisk logga). Vid en enda förening
  // (eller förbundsläge) behövs ingen banner – previewen visar loggan direkt med sin egen not.
  const showForeningBanner = useLocalAssLogo && multipleForeningar;

  // "Visa exakt PDF": renderar en riktig faktura-PDF (fiktiva rader) via API:et utifrån valt formulär + texter och
  // öppnar den i en ny flik. Kräver ingen sparad mall (POST-preview skriver inget i databasen). localAssIdArg = vald
  // förenings logga (i föreningsläge) – väljs vid klick när urvalet omfattar flera föreningar.
  const [pdfLoading, setPdfLoading] = useState(false);
  const openExactPdf = async (localAssIdArg?: number) => {
    if (!ownerId || ownerId <= 0) return;
    setPdfLoading(true);
    try {
      const res = await createInvoiceTemplatePreviewDraft({
        formTemplate: formTemplate || 'bgocr_2024',
        invoiceType: 'main',
        invoiceHeading,
        invoiceText,
        ownerId,
        localAssId: localAssIdArg,
      });
      const blob = new Blob([new Uint8Array(res.data)], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setPdfLoading(false);
    }
  };
  // I föreningsläge med flera föreningar väljer man vilken förenings logga PDF:en ska renderas med innan den öppnas;
  // annars öppnas den direkt (förbundets logga, eller den enda föreningens).
  const [pdfForeningOpen, setPdfForeningOpen] = useState(false);
  const [pdfForeningId, setPdfForeningId] = useState<number | null>(null);
  const handlePdfClick = () => {
    if (useLocalAssLogo && multipleForeningar) {
      setPdfForeningId(selectedLocalAssId ?? foreningList[0]?.localAssId ?? null);
      setPdfForeningOpen(true);
    } else {
      openExactPdf(previewLocalAssId);
    }
  };

  // Testutskick: skicka ett riktigt mejl (e-postrubrik + text) med faktura-PDF:en bifogad till angiven adress. I
  // föreningsläge med flera föreningar väljer man förening i modalen innan man skickar.
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testEmail, setTestEmail] = useState(currentUser?.email ?? '');
  const [testSending, setTestSending] = useState(false);
  const [testForeningId, setTestForeningId] = useState<number | null>(null);
  const openTestModal = () => {
    setTestForeningId(selectedLocalAssId ?? foreningList[0]?.localAssId ?? null);
    setTestModalOpen(true);
  };
  const sendTest = async () => {
    if (!ownerId || ownerId <= 0 || !testEmail.trim()) return;
    setTestSending(true);
    try {
      await sendInvoiceTemplateTestEmail({
        toEmail: testEmail.trim(),
        ownerId,
        formTemplate: formTemplate || 'bgocr_2024',
        emailHeading,
        emailText,
        invoiceHeading,
        invoiceText,
        localAssId: useLocalAssLogo && testForeningId ? testForeningId : undefined,
      });
      if (isMounted()) {
        toast.success(`Testutskick skickat till ${testEmail.trim()}`);
        setTestModalOpen(false);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      if (isMounted()) setTestSending(false);
    }
  };

  // "Rensa alla fält": tillbaka till första valet (inget läge) och töm texterna + klarmarkeringarna. Blankettvalet
  // (egen flik) rörs inte.
  const resetTexts = () => {
    setValue('textsFromPrevious', false);
    setValue('textsWriteChosen', false);
    setValue('completeLater', false);
    setValue('emailHeading', '');
    setValue('emailText', '');
    setValue('invoiceHeading', '');
    setValue('invoiceText', '');
    setEmailTextConfirmed(false);
    setInvoiceTextConfirmed(false);
    setBlankettConfirmed(false);
    setTextTab(tabOrder[0]);
  };

  // Starta "hämta texter från tidigare avisering" på nytt: nollställ ALLA klarmarkeringar (även blankett) så kunden
  // tvingas igenom samtliga flikar igen, och öppna kopiera-modalen.
  const startCopyFromPrevious = () => {
    setValue('completeLater', false);
    setEmailTextConfirmed(false);
    setInvoiceTextConfirmed(false);
    setBlankettConfirmed(false);
    setTextTab(tabOrder[0]);
    setCopyOpen(true);
  };

  // "Jag kompletterar senare"-kryssrutan – används både i första valet och i den kompakta raden efter valet.
  const completeLaterCheckbox = (
    <FormControlLabel
      sx={{ ml: 0 }}
      control={
        <Checkbox
          size='small'
          sx={{ pr: 0.5 }}
          checked={completeLater}
          onChange={(e) => {
            setValue('completeLater', e.target.checked);
            // Texterna är inte klara när de ska kompletteras senare – nollställ klar-markeringarna.
            if (e.target.checked) {
              setEmailTextConfirmed(false);
              setInvoiceTextConfirmed(false);
            }
          }}
        />
      }
      label='Jag kompletterar senare'
    />
  );

  // Kompakt åtgärdsrad (efter valet): rensa, hämta från tidigare, komplettera senare. Ligger högerställd på flikraden
  // (eller ovanför komplettera-senare-varningen) för att spara höjd.
  const compactActions = (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <Button variant='text' size='small' startIcon={<RestartAltOutlined />} onClick={resetTexts} sx={{ textTransform: 'none' }}>
        Rensa fält
      </Button>
      {/* "Hämta från tidigare avisering" visas bara på fliken Fakturans innehåll – döljs på övriga flikar. */}
      {effectiveTextTab === 'invoice' && (
        <Button variant='text' size='small' startIcon={<HistoryOutlined />} onClick={startCopyFromPrevious} sx={{ textTransform: 'none' }}>
          Hämta från tidigare avisering
        </Button>
      )}
    </Box>
  );

  // Förhandsvisning av e-postmeddelandet (mock, drivs av emailHeading/emailText).
  const emailPreview = (
    // zoom krymper hela mejlkortet (både layout och innehåll) så previewen blir kompakt även för stora mejltexter/bilder.
    <Box
      sx={{
        border: 1,
        borderColor: 'grey.300',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: '#fff',
        boxShadow: 3,
        maxWidth: 400,
        mx: 'auto',
        zoom: 0.8,
      }}
    >
      <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1.5, borderBottom: 1, borderColor: 'grey.300' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <EmailIcon fontSize='small' color='action' />
          <Typography variant='caption' fontWeight={600} color='text.secondary'>
            Från: {currentUser?.customerName || ''}
          </Typography>
        </Box>
        <Typography variant='body2' fontWeight={600}>
          {emailHeading || (
            <Box component='span' sx={{ color: 'text.disabled' }}>
              Ingen rubrik angiven
            </Box>
          )}
        </Typography>
        {/* Bilaga visas högst upp (nära ämne/avsändare), som i en e-postklient. */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1,
            px: 1,
            py: 0.25,
            bgcolor: '#fff',
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
          }}
        >
          <PictureAsPdfIcon fontSize='small' color='error' />
          <Typography variant='caption' color='text.secondary'>
            Faktura.pdf
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: 2, py: 2 }}>
        {/* Logotyp överst i mejlet (som i det skarpa utskicket). Loggan att visa (previewLogoSrc) är förbundets logga i
            förbundsläge och den valda föreningens logga i föreningsläge. Saknas logga visas placeholder + info-not. */}
        <Box sx={{ mb: 1.5 }}>
          {previewLogoSrc ? (
            <Box component='img' src={previewLogoSrc} alt='Logotyp' sx={{ maxHeight: 32, maxWidth: 150 }} />
          ) : (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 90,
                height: 30,
                border: '1px dashed',
                borderColor: 'grey.400',
                borderRadius: 0.5,
              }}
            >
              <Typography variant='caption' color='text.disabled'>
                LOGO
              </Typography>
            </Box>
          )}
          {useLocalAssLogo && (
            <Typography variant='caption' color='primary.main' sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              {selectedForening && previewLogoSrc
                ? `Förhandsgranskning med ${selectedForening.name}s logga. Vid utskicket används varje medlems förenings logga.`
                : 'Logotypen byts till medlemmens förenings logga vid utskicket.'}
            </Typography>
          )}
        </Box>
        {emailText ? (
          // eslint-disable-next-line react/no-danger
          <Typography
            variant='body2'
            color='text.secondary'
            component='div'
            // Innesluter kundens egen CSS (t.ex. vattenmärken med position: fixed) i rutan.
            sx={{ contain: 'paint', position: 'relative', overflow: 'hidden' }}
            dangerouslySetInnerHTML={{ __html: emailText }}
          />
        ) : (
          <Typography variant='body2' color='text.disabled'>
            Ingen text angiven
          </Typography>
        )}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: 'grey.700',
            color: '#fff',
            px: 2,
            py: 0.75,
            borderRadius: 1,
            fontSize: '0.8rem',
            fontWeight: 600,
            mt: 2,
          }}
        >
          Gå till fakturan och betala här
        </Box>
      </Box>
    </Box>
  );

  // Höger kolumn: förhandsvisning i ljusgrå ruta (delas av text- och blankett-flikarna). basis styr bredden.
  const previewPane = (preview: ReactNode, basis = '40%'): ReactNode => (
    <Box sx={{ flex: { md: `0 0 ${basis}` }, minWidth: 0, position: { md: 'relative' } }}>
      <Box
        sx={{
          borderLeft: '1px solid',
          borderColor: 'divider',
          pt: 0,
          px: 4,
          pb: 4,
          position: { md: 'absolute' },
          inset: { md: 0 },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>
          Förhandsvisning
        </Typography>
        {/* Grå "iframe"-yta så den vita previewen framhävs (samma som Förhandsgranska-fliken). */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1, p: 2 }}>{preview}</Box>
      </Box>
    </Box>
  );

  // En sending-rad: rubrik/hjälptext + Klar högst upp, sedan rubrikfält + editor (utan kort-ram); förhandsvisning till
  // höger. Klar-knappen finns både högst upp (syns direkt) och längst ner (efter editorn).
  const sendingRow = (opts: {
    label: string;
    helper?: string;
    headingName: string;
    editor: ReactNode;
    preview: ReactNode;
    confirmed: boolean;
    setConfirmed: (b: boolean) => void;
    canConfirm: boolean;
  }): ReactNode => {
    const klarButton = (size: 'small' | 'medium') =>
      opts.confirmed ? (
        <Button variant='outlined' size={size} onClick={() => opts.setConfirmed(false)}>
          Ändra
        </Button>
      ) : (
        <Button
          variant='contained'
          size={size}
          disableElevation
          disabled={!opts.canConfirm}
          startIcon={<CheckCircle />}
          onClick={() => opts.setConfirmed(true)}
        >
          Klar
        </Button>
      );

    return (
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch', mb: 3, minHeight: { md: 720 } }}>
        {/* Vänster: rubrik + hjälptext + Klar högst upp (utanför), sedan rubrikfält + editor. När texten är klarmarkerad
            läggs ett grönt kort runt SJÄLVA innehållet (rubrikfält + editor) – rubrik/Klar ligger utanför, som i flik 3. */}
        <Box sx={{ flex: { md: '0 0 58%' }, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {opts.label}
              </Typography>
              {opts.helper && (
                <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
                  {opts.helper}
                </Typography>
              )}
            </Box>
            {klarButton('medium')}
          </Box>
          <Box sx={opts.confirmed ? { ...cardStyle(true), mt: 2 } : { mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <ControlledTextField name={opts.headingName} label='Rubrik' fullWidth disabled={opts.confirmed} />
            </Box>
            {opts.editor}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>{klarButton('medium')}</Box>
        </Box>

        {/* Höger: förhandsvisning, scroll vid behov */}
        {previewPane(opts.preview)}
      </Box>
    );
  };

  return (
    <GridWrapper>
      {/* Scroll-markör: när man klarmarkerar en flik scrollas hit så man ser att nästa flik aktiveras. */}
      <Box ref={topRef} sx={{ position: 'absolute', top: -8 }} />
      {/* Första valet (och komplettera-senare-läget): rubrik + hur texterna ska läggas in. I första valet centreras
          blocket; i komplettera-senare-läget visas knapparna vänsterställt ovanför varningen. */}
      {(noneChosen || completeLater) && (
        <Grid item xs={12}>
          {/* Centrerat både i första valet och i komplettera-senare-läget (samma vy), så inget hoppar/rubrik försvinner. */}
          <Box sx={{ maxWidth: 760, mx: 'auto', textAlign: 'center', mt: { md: 4 } }}>
            <Typography variant='h5' sx={{ fontWeight: 700, mb: 0.5 }}>
              Vad ska stå i utskicket?
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2.5 }}>
              I detta steg anger du texterna för {showEmail ? `${emailChannelMsg} och ` : ''}fakturans innehåll.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              <Button
                variant='contained'
                disableElevation
                startIcon={<HistoryOutlined />}
                onClick={startCopyFromPrevious}
                sx={{ textTransform: 'none' }}
              >
                Hämta texterna från tidigare avisering
              </Button>
              <Button
                variant='outlined'
                disableElevation
                onClick={() => {
                  setValue('completeLater', false);
                  setValue('textsFromPrevious', false);
                  setValue('textsWriteChosen', true);
                }}
                sx={{ textTransform: 'none' }}
              >
                Jag skriver texter
              </Button>
              {completeLaterCheckbox}
            </Box>
          </Box>
        </Grid>
      )}

      {completeLater && (
        <Grid item xs={12}>
          <Alert severity='error'>
            Texterna måste vara inlagda och klarmarkerade senast{' '}
            <b>{toCustomerSendDate(sendDate)?.subtract(7, 'day').format('YYYY-MM-DD') ?? '7 dagar före utskicksdatumet'}</b> (7 dagar före
            utskicksdatumet). Annars behandlas inte beställningen och ett nytt utskicksdatum måste väljas för att göra utskicket senare.
          </Alert>
        </Grid>
      )}

      {!completeLater && (textsFromPrevious || writeChosen) && (
        <Grid item xs={12}>
          {/* Guidad, sekventiell flikrad med åtgärderna högerställda på samma rad (sparar höjd). Man klarmarkerar en
              flik i taget, kommande flikar är låsta tills dess. Ingen röd "fel"-markering – bara grön bock på klara,
              hänglås på låsta, och Förhandsgranska sist. */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tabs
              value={effectiveTextTab}
              onChange={(_e, v) => {
                if (!isTabLocked(v)) setTextTab(v as TextTabKey);
              }}
              variant='scrollable'
              scrollButtons='auto'
              sx={{ minHeight: 44, flex: 1, minWidth: 0, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
            >
              {tabOrder.map((key) => {
                const labels: Record<TextTabKey, string> = {
                  email: emailTabLabel,
                  invoice: 'Fakturans innehåll',
                  blankett: 'Fakturablankett',
                  preview: 'Förhandsgranska',
                };
                const locked = isTabLocked(key);
                const confirmed = isContentTab(key) && confirmedOf[key];
                let icon;
                if (confirmed) icon = <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />;
                else if (locked) icon = <LockOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />;
                else if (key === 'preview') icon = <VisibilityOutlined sx={{ fontSize: 18 }} />;
                else icon = <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.disabled' }} />;
                return <Tab key={key} value={key} disabled={locked} iconPosition='end' icon={icon} label={labels[key]} sx={{ minHeight: 44 }} />;
              })}
            </Tabs>
            <Box sx={{ flexShrink: 0, pb: 0.5 }}>{compactActions}</Box>
          </Box>

          {/* Innehållet tonas in vid flikbyte så övergången (klarmarkerad → nästa flik) känns lugn, inte hackig. */}
          <Fade in key={effectiveTextTab} timeout={300}>
            {/* Lite luft runt allt flik-innehåll. */}
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
              {effectiveTextTab === 'email' &&
                showEmail &&
                sendingRow({
                  label: `Vad ska stå i ${emailChannelTitle}?`,
                  helper: 'Fyll i rubrik och text nedan. Tryck därefter Klar.',
                  headingName: 'emailHeading',
                  editor: <ControlledTextEditor adminInvoiceEmailSection rows={28} name='emailText' disabled={emailTextConfirmed} />,
                  preview: emailPreview,
                  confirmed: emailTextConfirmed,
                  setConfirmed: confirmEmail,
                  canConfirm: emailText.trim().length > 0,
                })}
              {effectiveTextTab === 'invoice' &&
                sendingRow({
                  label: 'Vad ska stå i fakturan?',
                  helper: 'Fyll i rubrik och text nedan. Tryck därefter Klar.',
                  headingName: 'invoiceHeading',
                  editor: (
                    <ControlledTextEditor
                      adminInvoice
                      rows={28}
                      name='invoiceText'
                      disabled={invoiceTextConfirmed}
                      toolbarOverride='bold italic underline'
                      hideMenuBar
                    />
                  ),
                  preview: (
                    <SendingInvoiceMock
                      highlight='text'
                      formTemplate={formTemplate}
                      invoiceHeading={invoiceHeading}
                      invoiceText={invoiceText}
                      accountNr={accountNr}
                      senderName={currentUser?.customerName}
                      useLocalAssLogo={useLocalAssLogo}
                      logoSrc={previewLogoSrc}
                    />
                  ),
                  confirmed: invoiceTextConfirmed,
                  setConfirmed: confirmInvoice,
                  canConfirm: invoiceText.trim().length > 0,
                })}
              {effectiveTextTab === 'blankett' && (
                <Box
                  sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch', mb: 3, minHeight: { md: 720 } }}
                >
                  {/* Vänster: val av blankett – större alternativkort (som val av leveransmetod), inte i en textruta */}
                  <Box sx={{ flex: { md: '0 0 55%' }, minWidth: 0 }}>
                    {/* Rubrik + Klar/Ändra högst upp så knappen syns direkt. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                      <Typography variant='h6' sx={{ fontWeight: 700 }}>
                        Vilken fakturablankett?
                      </Typography>
                      {blankettConfirmed ? (
                        <Button variant='outlined' size='medium' onClick={() => setBlankettConfirmed(false)}>
                          Ändra
                        </Button>
                      ) : (
                        <Button
                          variant='contained'
                          size='medium'
                          disableElevation
                          disabled={!formTemplate}
                          startIcon={<CheckCircle />}
                          onClick={() => {
                            setBlankettConfirmed(true);
                            goToNextTab('blankett');
                          }}
                        >
                          Klar
                        </Button>
                      )}
                    </Box>
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                      Välj inbetalningskort. Valen styrs av faktureringsgruppens kontotyp ({accountTypeLabel}).
                    </Typography>
                    {accountType == null ? (
                      <Skeleton variant='rounded' height={72} sx={{ borderRadius: 2 }} />
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {blankettOptions.map((o) => {
                          const selected = formTemplate === o.code;
                          // Vald + klarmarkerad = grön (som övriga klara rutor); vald men ej klar = primärfärg; annars neutral.
                          const done = selected && blankettConfirmed;
                          const accent = done ? 'success.main' : 'primary.main';
                          const OptIcon = o.code === BLANK_OPTION.code ? BlockOutlined : ReceiptLongOutlined;
                          return (
                            <Box
                              key={o.code}
                              role='button'
                              onClick={() => !blankettConfirmed && setValue('formTemplate', o.code)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                border: '2px solid',
                                borderColor: selected ? accent : 'divider',
                                borderRadius: 2,
                                // Grön ton = samma som övriga klara kort i guiden (cardStyle): alpha(success.light, 0.2).
                                bgcolor: (theme) => {
                                  if (done) return alpha(theme.palette.success.light, 0.2);
                                  if (selected) return theme.palette.action.selected;
                                  return theme.palette.background.paper;
                                },
                                cursor: blankettConfirmed ? 'default' : 'pointer',
                                opacity: blankettConfirmed && !selected ? 0.5 : 1,
                                transition: 'border-color .15s, background-color .15s',
                                '&:hover': blankettConfirmed ? {} : { borderColor: 'primary.main' },
                              }}
                            >
                              <OptIcon sx={{ fontSize: 30, color: selected ? accent : 'action.active' }} />
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700 }}>{o.label}</Typography>
                                <Typography variant='body2' color='text.secondary'>
                                  {o.description}
                                </Typography>
                              </Box>
                              {selected && <CheckCircle sx={{ ml: 'auto', color: accent }} />}
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    {/* Logotyp på fakturan: förbundets (centrala) logga eller föreningarnas egna logga. Visas bara för
                        förbund (flera föreningar) – en enskild förening använder förbundets logga utan val. Låses (som
                        blankettvalet) när blanketten är klarmarkerad. */}
                    {showLogoChoice && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant='subtitle1' sx={{ fontWeight: 700, mb: 0.5 }}>
                          Logotyp
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                          Välj vilken logotyp som ska tryckas på fakturan.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {[
                            {
                              local: false,
                              label: 'Förbundets logga',
                              desc: 'Den logga som är uppsatt centralt för förbundet.',
                            },
                            {
                              local: true,
                              label: 'Föreningens egna logga',
                              desc: 'Varje medlem får sin förenings egna logga vid utskicket.',
                            },
                          ].map((o) => {
                            const selected = useLocalAssLogo === o.local;
                            const done = selected && blankettConfirmed;
                            const accent = done ? 'success.main' : 'primary.main';
                            return (
                              <Box
                                key={o.label}
                                role='button'
                                onClick={() => !blankettConfirmed && setValue('useLocalAssLogo', o.local)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  p: 2,
                                  border: '2px solid',
                                  borderColor: selected ? accent : 'divider',
                                  borderRadius: 2,
                                  bgcolor: (theme) => {
                                    if (done) return alpha(theme.palette.success.light, 0.2);
                                    if (selected) return theme.palette.action.selected;
                                    return theme.palette.background.paper;
                                  },
                                  cursor: blankettConfirmed ? 'default' : 'pointer',
                                  opacity: blankettConfirmed && !selected ? 0.5 : 1,
                                  transition: 'border-color .15s, background-color .15s',
                                  '&:hover': blankettConfirmed ? {} : { borderColor: 'primary.main' },
                                }}
                              >
                                {/* Vänster: riktig förbundslogga (om den finns) resp. ikon för föreningens logga. */}
                                <Box
                                  sx={{
                                    width: 64,
                                    height: 40,
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    bgcolor: '#fff',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {(() => {
                                    if (o.local) return <GroupsOutlined sx={{ fontSize: 26, color: 'action.active' }} />;
                                    if (forbundLogoSrc)
                                      return (
                                        <Box
                                          component='img'
                                          src={forbundLogoSrc}
                                          alt='Förbundets logga'
                                          sx={{ maxWidth: '100%', maxHeight: '100%' }}
                                        />
                                      );
                                    return <ImageOutlined sx={{ fontSize: 26, color: 'action.active' }} />;
                                  })()}
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 700 }}>{o.label}</Typography>
                                  <Typography variant='body2' color='text.secondary'>
                                    {o.desc}
                                  </Typography>
                                </Box>
                                {selected && <CheckCircle sx={{ ml: 'auto', color: accent }} />}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      {blankettConfirmed ? (
                        <Button variant='outlined' size='medium' onClick={() => setBlankettConfirmed(false)}>
                          Ändra
                        </Button>
                      ) : (
                        <Button
                          variant='contained'
                          size='medium'
                          disableElevation
                          disabled={!formTemplate}
                          startIcon={<CheckCircle />}
                          onClick={() => {
                            setBlankettConfirmed(true);
                            goToNextTab('blankett');
                          }}
                        >
                          Klar
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {/* Höger: förhandsvisning av blanketten på PDF-fakturan (blankett-ytan markerad) */}
                  {previewPane(
                    <SendingInvoiceMock
                      highlight='slip'
                      formTemplate={formTemplate}
                      invoiceHeading={invoiceHeading}
                      invoiceText={invoiceText}
                      accountNr={accountNr}
                      senderName={currentUser?.customerName}
                      useLocalAssLogo={useLocalAssLogo}
                      logoSrc={previewLogoSrc}
                    />,
                    '43%',
                  )}
                </Box>
              )}

              {effectiveTextTab === 'preview' && (
                <Box>
                  {/* Info-banner (föreningsläge): förklarar vilken logga previewen visar. Vid flera föreningar sker
                      själva föreningsvalet vid klick på testutskick/PDF – ingen dropdown här. */}
                  {showForeningBanner && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                      }}
                    >
                      <GroupsOutlined color='action' />
                      <Box sx={{ flex: 1, minWidth: 220 }}>
                        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                          Flera föreningar i urvalet
                        </Typography>
                        <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                          Du väljer förening när du skickar testutskick eller öppnar PDF. Skärm-previewen visar en generisk logotyp eftersom loggan
                          byts per medlem.
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {/* Två kolumner: e-postmeddelandet (med testutskick) och PDF-fakturan (med förhandsgranska-PDF). */}
                  <Box
                    sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch', mb: 3, minHeight: { md: 720 } }}
                  >
                    {showEmail && (
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          maxWidth: { md: '50%' },
                          pr: { md: 2 },
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                          <Typography variant='h6' sx={{ fontWeight: 700 }}>
                            E-postmeddelandet
                          </Typography>
                          <Button
                            variant='outlined'
                            startIcon={<EmailIcon />}
                            onClick={openTestModal}
                            disabled={!ownerId}
                            sx={{ textTransform: 'none', flexShrink: 0 }}
                          >
                            Skicka testutskick
                          </Button>
                        </Box>
                        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                          Så här ser mejlet ut för medlemmen. Skicka ett testutskick till dig själv för att se det på riktigt.
                        </Typography>
                        {/* Grå "iframe"-yta så den vita e-post-previewen framhävs. */}
                        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1, p: 2 }}>{emailPreview}</Box>
                      </Box>
                    )}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: { md: '50%' },
                        pl: { md: 2 },
                        // Vertikal avgränsare mellan kolumnerna (bara när e-postkolumnen finns), som på övriga flikar.
                        // Longhand så färgen inte återställs till svart av borderLeft-shorthand inne i media-queryn.
                        borderLeftWidth: showEmail ? { md: '1px' } : 0,
                        borderLeftStyle: 'solid',
                        borderLeftColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 0.25 }}>
                        <Typography variant='h6' sx={{ fontWeight: 700 }}>
                          Fakturan (PDF)
                        </Typography>
                        <Button
                          variant='outlined'
                          startIcon={pdfLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
                          onClick={handlePdfClick}
                          disabled={pdfLoading || !ownerId}
                          sx={{ textTransform: 'none', flexShrink: 0 }}
                        >
                          Förhandsgranska fakturan (PDF)
                        </Button>
                      </Box>
                      <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
                        Så här ser den bifogade/utskrivna fakturan ut. Förhandsgranska fakturan för exakt återgivning.
                      </Typography>
                      {/* Grå "iframe"-yta så den vita faktura-previewen framhävs. */}
                      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1, p: 2 }}>
                        <SendingInvoiceMock
                          formTemplate={formTemplate}
                          invoiceHeading={invoiceHeading}
                          invoiceText={invoiceText}
                          accountNr={accountNr}
                          senderName={currentUser?.customerName}
                          width={300}
                          useLocalAssLogo={useLocalAssLogo}
                          logoSrc={previewLogoSrc}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Fade>
        </Grid>
      )}

      <SendingCopyTextsFromMissionModal open={copyOpen} onClose={() => setCopyOpen(false)} showEmail={showEmail} />

      {/* Testutskick-modal: ange mottagare och skicka ett riktigt mejl med fakturan bifogad (som i Kommunikation). */}
      <Dialog open={testModalOpen} onClose={() => setTestModalOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Skicka testutskick</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Ett testmejl skickas med fakturan bifogad – precis som en medlem skulle få det. Avsändaren är densamma som vid ett skarpt utskick.
          </Typography>
          {useLocalAssLogo && multipleForeningar && (
            <TextField
              select
              fullWidth
              label='För vilken förening?'
              value={testForeningId ?? ''}
              onChange={(e) => setTestForeningId(Number(e.target.value) || null)}
              helperText='Testutskicket skickas med den valda föreningens logga.'
              sx={{ mb: 2 }}
            >
              {foreningList.map((a) => (
                <MenuItem key={a.localAssId} value={a.localAssId}>
                  {a.name}
                  {!a.hasLogo ? ' (ingen egen logga)' : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            autoFocus
            fullWidth
            type='email'
            label='Mottagarens e-postadress'
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && testEmail.trim() && !testSending) sendTest();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant='text' onClick={() => setTestModalOpen(false)} disabled={testSending}>
            Avbryt
          </Button>
          <Button
            variant='contained'
            disableElevation
            startIcon={testSending ? <CircularProgress size={16} /> : <EmailIcon />}
            disabled={testSending || !testEmail.trim() || !ownerId}
            onClick={sendTest}
          >
            Skicka
          </Button>
        </DialogActions>
      </Dialog>

      {/* Välj vilken förenings logga PDF:en ska förhandsgranskas med (föreningsläge med flera föreningar). */}
      <Dialog open={pdfForeningOpen} onClose={() => setPdfForeningOpen(false)} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>För vilken förening vill du förhandsgranska?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Fakturan renderas med den valda föreningens logga.
          </Typography>
          <TextField select fullWidth label='Förening' value={pdfForeningId ?? ''} onChange={(e) => setPdfForeningId(Number(e.target.value) || null)}>
            {foreningList.map((a) => (
              <MenuItem key={a.localAssId} value={a.localAssId}>
                {a.name}
                {!a.hasLogo ? ' (ingen egen logga)' : ''}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant='text' onClick={() => setPdfForeningOpen(false)} disabled={pdfLoading}>
            Avbryt
          </Button>
          <Button
            variant='contained'
            disableElevation
            startIcon={pdfLoading ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
            disabled={pdfLoading || !pdfForeningId}
            onClick={() => {
              setPdfForeningOpen(false);
              openExactPdf(pdfForeningId ?? undefined);
            }}
          >
            Förhandsgranska
          </Button>
        </DialogActions>
      </Dialog>
    </GridWrapper>
  );
};
